import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import API from "../axios";

const VehicleVariantsWithoutGallery = () => {
    const params = new URLSearchParams(window.location.search);
    const [page, setPage] = useState<number>(Number(params.get("page")) || 1);
    const [searchString, setSearchString] = useState(
        params.get("searchString") || ""
    );
    const [limit] = useState(10); // Should match backend default
    const [filters, setFilters] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [vehicleVariants, setVehicleVariants] = useState<Array<any>>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const updateQueryParams = (updates: Record<string, string>) => {
        const url = new URL(window.location.href);
        Object.entries(updates).forEach(([key, value]) => {
            if (!value || value === "null" || value === "undefined") {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, value);
            }
        });
        window.history.pushState({}, "", url);
    };

    const loadVehiclesFromUrl = async () => {
        const paramsUrl = new URLSearchParams(window.location.search);
        const currentPage = Number(paramsUrl.get("page")) || 1;
        const currentSearchString = paramsUrl.get("searchString") || "";

        const query: any = {
            page: currentPage,
            limit,
            sortBy: "title",
            sortOrder: "asc",
        };

        // Always include search string if present
        if (currentSearchString) {
            query.searchString = currentSearchString;
        }

        setLoading(true);
        try {
            const result: any = await API.paginateVehicleVariantsWithoutGallery(
                query
            );
            setVehicleVariants(result.data);
            setPage(currentPage);
            setTotalPages(result.pagination.totalPages);
            setTotalItems(result.pagination.total);

            // Update filters for display
            setFilters({
                ...(query.searchString && { searchString: query.searchString }),
            });
        } catch (error) {
            console.error("Failed to load vehicles:", error);
        } finally {
            setLoading(false);
        }
    };

    const goToPage = async (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;

        updateQueryParams({
            page: newPage.toString(),
            searchString,
        });

        await loadVehiclesFromUrl();
    };

    // Debounced search handler - now applies all filters together
    useEffect(() => {
        const timer = setTimeout(() => {
            updateQueryParams({
                searchString,
                page: "1",
            });
            loadVehiclesFromUrl();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchString]); // Only trigger on search string change

    // Load data on initial render
    useEffect(() => {
        loadVehiclesFromUrl();
    }, []);

    const activeFiltersCount = Object.keys(filters).filter(
        (key) => filters[key] !== undefined && filters[key] !== ""
    ).length;

    return (
        <div>
            <Container style={{ marginTop: 100 }}>
                <Row>
                    <Col cols="6" className="d-flex align-items-center gap-2">
                        <a
                            className="btn btn-outline-black btn-md mx-1"
                            href={`/vehicles`}
                        >
                            <i className="bi bi-chevron-left fs-4"></i>
                        </a>
                        <h3 className="fw-medium text-primary">
                            Variants Without Gallery
                        </h3>
                    </Col>
                    <Col cols="6" className="text-end">
                        <Row className="g-2">
                            <Col
                                cols="12"
                                className="d-flex justify-content-end gap-2"
                            >
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search vehicles..."
                                    style={{ maxWidth: "250px" }}
                                    value={searchString}
                                    onChange={(e) =>
                                        setSearchString(e.target.value)
                                    }
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row>
                    <Col cols="12" className="d-flex justify-content-end"></Col>
                </Row>
                <Row className="mt-5">
                    {loading ? (
                        <div
                            className="d-flex justify-content-center my-5"
                            style={{ minHeight: "100vh" }}
                        >
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>
                    ) : (
                        <table className="table mt-3">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Title</th>
                                    <th>Variant</th>
                                    <th>Available For Sale</th>
                                    <th>Forecased Stock</th>
                                    <th>Gallery Photos</th>
                                    <th>Edit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicleVariants.map((vehicle, index) => (
                                    <tr key={index}>
                                        <td>
                                            <img
                                                src={
                                                    vehicle.productImage ||
                                                    "/images/no-image.png"
                                                }
                                                width={80}
                                                height={60}
                                                className="rounded-2"
                                            />
                                        </td>
                                        <td className="fw-normal">
                                            {vehicle.title}
                                        </td>
                                        <td>
                                            {vehicle.productAttributes.map(
                                                (productAttribute: any) => (
                                                    <div className="badge bg-primary mx-1">
                                                        {`${productAttribute.attributeName} : ${productAttribute.attributeValueName}`}
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td>
                                            <strong>
                                                {vehicle.validStock}
                                            </strong>
                                        </td>
                                        <td>
                                            <strong>
                                                {vehicle.forecastedStock}
                                            </strong>
                                        </td>
                                        <td
                                            className={`fw-normal ${
                                                vehicle.productGallery.length ==
                                                0
                                                    ? "text-danger"
                                                    : "text-dark"
                                            }`}
                                        >
                                            {vehicle.productGallery.length}
                                        </td>
                                        <td>
                                            <a
                                                className="btn btn-outline-primary btn-sm mx-1"
                                                href={`/vehicle-variant/${vehicle._id}`}
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Updated Pagination */}
                    <nav
                        aria-label="Page navigation"
                        style={{ marginBottom: 200 }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                Showing{" "}
                                {vehicleVariants.length > 0
                                    ? (page - 1) * limit + 1
                                    : 0}{" "}
                                to {Math.min(page * limit, totalItems)}
                                of {totalItems} vehicles
                            </div>
                            <ul className="pagination">
                                <li
                                    className={`page-item ${
                                        page <= 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(1)}
                                        disabled={page <= 1}
                                    >
                                        First
                                    </button>
                                </li>
                                <li
                                    className={`page-item ${
                                        page <= 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page - 1)}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {/* Page numbers - show up to 5 around current page */}
                                {totalPages > 0 &&
                                    Array.from(
                                        { length: Math.min(5, totalPages) },
                                        (_, i) => {
                                            let pageNum: number;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }

                                            return (
                                                <li
                                                    key={i}
                                                    className={`page-item ${
                                                        page === pageNum
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() =>
                                                            goToPage(pageNum)
                                                        }
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        }
                                    )}

                                <li
                                    className={`page-item ${
                                        page >= totalPages ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page + 1)}
                                        disabled={page >= totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                                <li
                                    className={`page-item ${
                                        page >= totalPages ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(totalPages)}
                                        disabled={page >= totalPages}
                                    >
                                        Last
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </Row>
            </Container>
        </div>
    );
};

export default VehicleVariantsWithoutGallery;
